/**
 * Where our sign-up and sign-in links point.
 *
 * Since 2026-10-10 the app serves `/signup` and `/login` on invideo.io itself.
 * ai.invideo.io only sends them on: its first script moves the page to the same
 * path on invideo.io before the app loads. A link to ai.invideo.io/signup costs
 * every visitor that extra document, and the ones it doesn't move (about 4% on
 * Android, 1.5% on desktop) sign up and onboard on ai.invideo.io. So links to
 * the app's sign-in pages go to invideo.io directly.
 *
 * Only prod's ai.invideo.io maps to invideo.io: every other `NEXT_PUBLIC_AI_HOST`
 * (kizoa staging, a local app) is kept as it is. `NEXT_PUBLIC_APP_SIGNIN_HOST`
 * overrides it, e.g. to go back to ai.invideo.io without a code change.
 */

const AI_ORIGIN = 'https://ai.invideo.io';
const FRONTEND_ORIGIN = 'https://invideo.io';
/** The app's sign-in pages, which invideo.io serves. */
const SIGNIN_PATH = /^\/(?:signup|login)\/?$/;

/** The origin sign-up and sign-in links go to. */
export function appSigninHost(
  aiHost: string | undefined = process.env.NEXT_PUBLIC_AI_HOST,
  override: string | undefined = process.env.NEXT_PUBLIC_APP_SIGNIN_HOST,
): string {
  if (override) return override.replace(/\/+$/, '');
  const host = (aiHost || AI_ORIGIN).replace(/\/+$/, '');
  return host === AI_ORIGIN ? FRONTEND_ORIGIN : host;
}

/**
 * A link from the CMS or our own constants, with an ai.invideo.io sign-up or
 * sign-in page pointed at `appSigninHost()`. The query and the hash are kept.
 * Any other link, including another ai.invideo.io page, is returned unchanged.
 */
export function toAppSigninUrl(link: string, host = appSigninHost()): string {
  if (!link) return link;
  let url: URL;
  try {
    url = new URL(link);
  } catch {
    return link;
  }
  if (url.origin !== AI_ORIGIN || !SIGNIN_PATH.test(url.pathname)) return link;
  return `${host}${url.pathname}${url.search}${url.hash}`;
}

/**
 * The stored landing query (`localStorage` 'qs', via `localStorageWithExpiry`)
 * as a search string to put after the sign-in path: `""` or `"?..."`.
 *
 * This app stores `window.location.search`, a string. The other app on this
 * origin (iv-pro-landing-pages, `useOnPageLoad`) stores the same key as an
 * object of params. Concatenating that object gave links to
 * `/signup[object Object]`, which invideo.io answers with a 404; its params
 * (utm_*, click ids) are kept instead. Anything else is dropped.
 */
export function storedQuery(saved: unknown): string {
  if (typeof saved === 'string') {
    if (!saved || saved === '?') return '';
    return saved.startsWith('?') ? saved : `?${saved}`;
  }
  if (!saved || typeof saved !== 'object' || Array.isArray(saved)) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(saved as Record<string, unknown>)) {
    if (typeof value === 'string') params.append(key, value);
  }
  const search = params.toString();
  return search ? `?${search}` : '';
}
